import { useApolloClient } from '@apollo/client'
import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import { useState } from 'react'
import { isGlobalAudienceEnabled } from '../../../../constants/featureFlags'
import { useSetBaseMarketV2 } from '../../../../hooks/audience/setBaseMarketV2'
import { useMarketsSpecification } from '../../../../hooks/audience/useMarketsSpecification'
import { useOptionalWaveId } from '../../../../hooks/useOptionalWaveId'
import { useSurveyId } from '../../../../hooks/useSurveyId'

/**
 * Makes the route's market the base of its wave. A wave has no Project page,
 * so its base is chosen in the market's settings; a multi-market project keeps
 * choosing it on its Project page.
 */
export const useBaseMarketSetting = () => {
  const surveyId = useSurveyId()
  const enabled = !!useOptionalWaveId() && isGlobalAudienceEnabled()
  const { baseSurveyId, loading, error } = useMarketsSpecification(
    enabled ? surveyId : undefined
  )
  const setBaseMarket = useSetBaseMarketV2()
  const client = useApolloClient()
  const { enqueueSnackbar } = useSnackbar()
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)

  const makeBase = async () => {
    setSaving(true)
    try {
      // A refused change is reported by the mutation hook, which resolves.
      const result = await setBaseMarket(surveyId, true)
      if (!result.data) {
        return
      }
      // Each market caches its own aggregate, so evict the inactive ones too.
      await client
        .refetchQueries({
          include: ['marketsSpecificationV2'],
          updateCache: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'questionnaireAggV2' })
          }
        })
        .catch(() => {
          enqueueSnackbar(
            'The base market changed, but the page couldn’t refresh. Reload to see it.',
            { variant: SnackbarVariant.Warning }
          )
        })
    } finally {
      setSaving(false)
      setConfirming(false)
    }
  }

  return {
    enabled,
    hasBase: !!baseSurveyId,
    isBase: baseSurveyId === surveyId,
    // Unreadable is not "no base": choosing then would skip the confirm and
    // silently move the real base.
    unknown: loading || !!error,
    saving,
    confirming,
    // Replacing a base moves its global questions, so that is confirmed first.
    choose: () => (baseSurveyId ? setConfirming(true) : void makeBase()),
    confirm: () => void makeBase(),
    cancel: () => setConfirming(false)
  }
}
